// AI Property Transform Analytics Tracking
// Conversion funnel tracking for AI feature

import { db } from '../firebase-config';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

// Generate unique session ID
const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Get or create session ID
let currentSessionId = null;
const getSessionId = () => {
  if (!currentSessionId) {
    currentSessionId = sessionStorage.getItem('ai_session_id');
    if (!currentSessionId) {
      currentSessionId = generateSessionId();
      sessionStorage.setItem('ai_session_id', currentSessionId);
    }
  }
  return currentSessionId;
};

// Track event to Firebase
export const trackAIEvent = async (eventName, eventData = {}) => {
  try {
    const sessionId = getSessionId();
    
    const eventPayload = {
      event: eventName,
      sessionId,
      timestamp: serverTimestamp(),
      device: getDeviceType(),
      source: getSource(),
      ...eventData
    };

    // Log to console for debugging
    console.log('🔥 AI Event:', eventPayload);

    // Save to Firebase
    await addDoc(collection(db, 'ai_analytics'), eventPayload);

    // Also track to localStorage for quick access
    const events = JSON.parse(localStorage.getItem('ai_events') || '[]');
    events.push({
      ...eventPayload,
      timestamp: new Date().toISOString()
    });
    localStorage.setItem('ai_events', JSON.stringify(events.slice(-100))); // Keep last 100 events

  } catch (error) {
    console.error('Error tracking AI event:', error);
  }
};

// Get device type
const getDeviceType = () => {
  const width = window.innerWidth;
  if (width < 640) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// Get traffic source
const getSource = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('utm_source') || 
         params.get('source') || 
         document.referrer || 
         'direct';
};

// Event tracking functions for each funnel stage

// TOP FUNNEL
export const trackLandingView = () => {
  trackAIEvent('landing_view');
};

export const trackUploadStart = () => {
  trackAIEvent('upload_start');
};

export const trackUploadComplete = (imageCount, propertyData) => {
  trackAIEvent('upload_complete', {
    imageCount,
    hasAddress: !!propertyData.address,
    hasPrice: !!propertyData.price,
    hasArea: !!propertyData.area_m2,
    hasRooms: !!propertyData.rooms
  });
};

// AI FUNNEL
export const trackStyleSelected = (style) => {
  trackAIEvent('style_selected', { style });
};

export const trackAIGenerationStarted = (style) => {
  trackAIEvent('ai_generation_started', { style });
};

export const trackAIGenerationCompleted = (style, duration) => {
  trackAIEvent('ai_generation_completed', { 
    style, 
    duration_ms: duration 
  });
};

// VALUE FUNNEL
export const trackPriceResultViewed = (priceData) => {
  trackAIEvent('price_result_viewed', {
    currentPrice: priceData.current,
    optimizedPrice: priceData.optimized,
    impact: priceData.impact,
    impactPercentage: ((priceData.impact / priceData.current) * 100).toFixed(2)
  });
};

export const trackPublishClicked = () => {
  trackAIEvent('publish_clicked');
};

export const trackBoostClicked = () => {
  trackAIEvent('boost_clicked');
};

// REVENUE FUNNEL
export const trackLeadSubmitted = (leadData) => {
  trackAIEvent('lead_submitted', leadData);
};

export const trackConsultationRequested = () => {
  trackAIEvent('consultation_requested');
};

export const trackPaidFeatureStarted = (featureName, price) => {
  trackAIEvent('paid_feature_started', { 
    featureName, 
    price 
  });
};

export const trackPaidFeatureCompleted = (featureName, price, transactionId) => {
  trackAIEvent('paid_feature_completed', { 
    featureName, 
    price,
    transactionId
  });
};

// SUGGESTION TRACKING
export const trackSuggestionChecked = (suggestion) => {
  trackAIEvent('suggestion_checked', { suggestion });
};

export const trackSuggestionUnchecked = (suggestion) => {
  trackAIEvent('suggestion_unchecked', { suggestion });
};

// Get analytics summary
export const getAnalyticsSummary = () => {
  const events = JSON.parse(localStorage.getItem('ai_events') || '[]');
  
  const summary = {
    totalEvents: events.length,
    sessionId: getSessionId(),
    device: getDeviceType(),
    source: getSource(),
    funnel: {
      landingViews: events.filter(e => e.event === 'landing_view').length,
      uploadStarts: events.filter(e => e.event === 'upload_start').length,
      uploadCompletes: events.filter(e => e.event === 'upload_complete').length,
      styleSelections: events.filter(e => e.event === 'style_selected').length,
      aiGenerations: events.filter(e => e.event === 'ai_generation_completed').length,
      priceViews: events.filter(e => e.event === 'price_result_viewed').length,
      publishClicks: events.filter(e => e.event === 'publish_clicked').length,
      boostClicks: events.filter(e => e.event === 'boost_clicked').length,
      leads: events.filter(e => e.event === 'lead_submitted').length
    },
    conversionRates: {}
  };

  // Calculate conversion rates
  if (summary.funnel.landingViews > 0) {
    summary.conversionRates.uploadRate = 
      ((summary.funnel.uploadCompletes / summary.funnel.landingViews) * 100).toFixed(2) + '%';
  }
  
  if (summary.funnel.uploadCompletes > 0) {
    summary.conversionRates.aiGenerationRate = 
      ((summary.funnel.aiGenerations / summary.funnel.uploadCompletes) * 100).toFixed(2) + '%';
  }
  
  if (summary.funnel.aiGenerations > 0) {
    summary.conversionRates.publishRate = 
      ((summary.funnel.publishClicks / summary.funnel.aiGenerations) * 100).toFixed(2) + '%';
  }

  return summary;
};

// Clear session (for testing)
export const clearAISession = () => {
  sessionStorage.removeItem('ai_session_id');
  localStorage.removeItem('ai_events');
  currentSessionId = null;
};
